import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  CircularProgress,
  Typography,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ZoomIn as ViewIcon, Download as DownloadIcon, Edit as EditIcon } from "@mui/icons-material";

import { getMachines, readAsyncStorageValues, getAllServiceTickets } from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";
import DataGridTable from "component/DataGridTable";
import { exportToCsv } from "utils/utilFunctions";
import FilterHeaderDropdown from "component/ui/FilterHeaderDropdown";
import ColumnHeaderLabel from "component/ui/ColumnHeaderLabel";

const AllServiceTickets = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { pathname } = useLocation();
  const { mode } = useSelector((state) => state.globalState);

  const [searchParams] = useSearchParams();
  const [serviceTickets, setServiceTickets] = useState([]);
  const [showExportAlertDialog, setShowExportAlertDialog] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);

  const { user } = useSelector((state) => state.auth);

  const [filterOptions, setFilterOptions] = useState({
    overDue: false,
    upComingIn: null,
  });
  const [filters, setFilters] = useState({
    creator: "",
    assigner: "",
    technician: "",
    maintenanceCode: "",
    category: "",
    status: "",
    manufModel: "",
    vendor: "",
  });
  const { machinesData, appConstants, isLoading, allServiceTickets } = useSelector((state) => state.service);
  const currentAppUserId = appConstants?.appUsers?.find((appUser) => appUser.userName === user?.username)?.userId;
  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(getMachines());
  }, [dispatch, searchParams]);
  useEffect(() => {
    if (appConstants) dispatch(getAllServiceTickets());
  }, [dispatch, appConstants]);

  const getVendorDisplayName = useCallback(
    (ticket) => {
      const vendorId = ticket?.vendor?.id ?? ticket?.vendor;
      const customer = appConstants?.customers?.find((c) => c.id === vendorId);
      return customer?.customerName || "Internal";
    },
    [appConstants?.customers],
  );

  const { creators, supervisors, technicians, maintenanceCodes, categories, statuses, manufModels, vendors } = useMemo(() => {
    const creatorsSet = new Set();
    const supervisorsSet = new Set();
    const techniciansSet = new Set();
    const maintenanceCodesSet = new Set();
    const categoriesSet = new Set();
    const statusesSet = new Set();
    const manufModelsSet = new Set();
    const vendorsSet = new Set();

    (allServiceTickets?.data || []).forEach((ticket) => {
      const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);
      const assignerUser = appConstants?.appUsers?.find(
        (user) => ticket?.assignerId === user.userId && user.userRole === appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR,
      );
      const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assigneeId === user.userId);

      if (creatorUser?.name) creatorsSet.add(creatorUser.name);
      if (assignerUser) supervisorsSet.add(assignerUser.nameEmpno || assignerUser.name);
      if (assigneUser) techniciansSet.add(assigneUser.nameEmpno || assigneUser.name);
      if (ticket?.maintenanceCode) maintenanceCodesSet.add(ticket.maintenanceCode);
      if (ticket?.ticketCategory) categoriesSet.add(ticket.ticketCategory);
      if (ticket?.status) statusesSet.add(ticket.status);

      const manufacturer = ticket?.manufacturer || "";
      const modelName = ticket?.modelName || "";
      const combinedValue = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
      if (combinedValue) manufModelsSet.add(combinedValue);
      vendorsSet.add(getVendorDisplayName(ticket));
    });

    return {
      creators: Array.from(creatorsSet).sort((a, b) => a.localeCompare(b)),
      supervisors: Array.from(supervisorsSet).sort((a, b) => a.localeCompare(b)),
      technicians: Array.from(techniciansSet).sort((a, b) => a.localeCompare(b)),
      maintenanceCodes: Array.from(maintenanceCodesSet).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(categoriesSet).sort((a, b) => a.localeCompare(b)),
      statuses: Array.from(statusesSet).sort((a, b) => a.localeCompare(b)),
      manufModels: Array.from(manufModelsSet).sort((a, b) => a.localeCompare(b)),
      vendors: Array.from(vendorsSet).sort((a, b) => a.localeCompare(b)),
    };
  }, [allServiceTickets?.data, appConstants, getVendorDisplayName]);

  const loadData = useCallback(() => {
    const serviceTickets = [];
    allServiceTickets.data.forEach((ticket) => {
      const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assigneeId === user.userId);
      const assignerUser = appConstants?.appUsers?.find(
        (user) => ticket?.assignerId === user.userId && user.userRole === appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR,
      );
      const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);
      const manufacturer = ticket?.manufacturer || "";
      const modelName = ticket?.modelName || "";
      const combinedManufModel = manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;

      if (filters.creator && filters.creator !== (creatorUser?.name || "")) return;
      else if (filters.assigner && filters.assigner !== (assignerUser?.nameEmpno || assignerUser?.name || "")) return;
      else if (filters.technician && filters.technician !== (assigneUser?.nameEmpno || assigneUser?.name || "")) return;
      else if (filters.category && filters.category !== ticket?.ticketCategory) return;
      else if (filters.status && filters.status !== ticket?.status) return;
      else if (filters.maintenanceCode && filters.maintenanceCode !== ticket?.maintenanceCode) return;
      else if (filters.manufModel && filters.manufModel !== combinedManufModel) return;
      else if (filters.vendor && filters.vendor !== getVendorDisplayName(ticket)) return;
      else if (
        filterOptions &&
        filterOptions.upComingIn &&
        ticket.dueDate &&
        !moment(ticket.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d"))
      )
        return;
      else if (filterOptions && filterOptions.upComingIn && !ticket.dueDate) return;
      else if (
        filterOptions &&
        filterOptions.overDue &&
        !moment(moment().format("DD MMM YYYY")).isAfter(moment(ticket.dueDate).format("DD MMM YYYY"))
      )
        return;
      else
        return serviceTickets.push({
          id: ticket?.ticketId,
          ticketId: ticket?.ticketId,
          ticketNumber: ticket?.ticketNumber || "",
          creatorId: ticket?.creatorId,
          ticketCategory: ticket.ticketCategory,
          status: ticket.status,
          machineManufacturer: manufacturer,
          machineModel: modelName,
          manufModel: combinedManufModel,
          machineSerialNumber: ticket?.serialNumber || "",
          machineMaintenanceCode: ticket?.maintenanceCode || "",
          description: ticket?.description || "",
          dueDate: ticket.dueDate ? moment(ticket.dueDate).format("DD MMM YY") : null,
          dateCreated: moment(ticket.openDate).format("DD MMM YY"),
          scheduledDate: moment(ticket.scheduledDate).format("DD MMM YY"),
          priority: ticket.priority ? ticket.priority : null,
          vendor: ticket.vendor ? ticket.vendor : null,
          creator: creatorUser?.name || "",
          assigner: assignerUser?.name || "",
          technician: assigneUser?.name,
          safetyTicket: ticket?.safetyTicket ? ticket?.safetyTicket : null,
        });
    });
    setServiceTickets(serviceTickets?.sort((a, b) => b.ticketId - a.ticketId) || []);
    setLoading(false);
  }, [
    allServiceTickets.data,
    appConstants?.appUsers,
    appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR,
    filterOptions,
    filters,
    getVendorDisplayName,
  ]);

  useEffect(() => {
    if (allServiceTickets.data) loadData();
  }, [dispatch, searchParams, filterOptions, filters, allServiceTickets, pathname, loadData]);

  const renderGridActions = (data) => {
    const canEdit =
      user?.roles?.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR) || data.row.creatorId === currentAppUserId;

    return (
      <div>
        <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + data.row.ticketId)}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        {canEdit && (
          <IconButton
            sx={{ mr: 1 }}
            onClick={() => navigate(`/${Links.UPDATE_SERVICE_TICKET}/` + data.row.ticketId, { state: { enableEditing: true } })}
          >
            <Tooltip title="Edit">
              <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </Tooltip>
          </IconButton>
        )}
      </div>
    );
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleClearSearch = () => setSearchValue("");

  const clearAllFilters = () => {
    setFilters({
      creator: "",
      assigner: "",
      technician: "",
      maintenanceCode: "",
      category: "",
      status: "",
      manufModel: "",
      vendor: "",
    });
    setFilterOptions({
      overDue: false,
      upComingIn: null,
    });
    setSearchValue("");
  };

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const columnAttributes = [
    {
      field: "ticketId",
      headerName: "Ticket ID",
      minWidth: 72,
      flex: 0.45,
      renderHeader: () => <ColumnHeaderLabel label="Ticket ID" />,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.ticketId : params.row.ticketNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.ticketId : params.row.ticketNumber || params.row.ticketId;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.ticketId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Open Date",
      minWidth: 80,
      flex: 0.3,
      renderHeader: () => <ColumnHeaderLabel label="Open Date" />,
      sortComparator: dateComparator,
    },
    {
      field: "scheduledDate",
      headerName: "Sch. Date",
      minWidth: 77,
      flex: 0.3,
      renderHeader: () => <ColumnHeaderLabel label="Sch. Date" />,
      sortComparator: dateComparator,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 77,
      flex: 0.3,
      renderHeader: () => <ColumnHeaderLabel label="Due Date" />,
      sortComparator: dateComparator,
    },
    {
      field: "creator",
      headerName: "Creator",
      minWidth: 109,
      flex: 0.6,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Creator"
          value={filters.creator}
          options={creators}
          onChange={(value) => setFilters({ ...filters, creator: value })}
        />
      ),
    },
    {
      field: "assigner",
      headerName: "Supervisor",
      minWidth: 124,
      flex: 0.5,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Supervisor"
          value={filters.assigner}
          options={supervisors}
          onChange={(value) => setFilters({ ...filters, assigner: value })}
        />
      ),
    },
    {
      field: "technician",
      headerName: "Tech",
      minWidth: 120,
      flex: 0.3,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Tech"
          value={filters.technician}
          options={technicians}
          onChange={(value) => setFilters({ ...filters, technician: value })}
        />
      ),
    },
    {
      field: "manufModel",
      headerName: "Manf & Model",
      minWidth: 120,
      flex: 0.5,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Manf & Model"
          value={filters.manufModel}
          options={manufModels}
          onChange={(value) => setFilters({ ...filters, manufModel: value })}
        />
      ),
      valueGetter: (params) => {
        const manufacturer = params.row.machineManufacturer || "";
        const modelName = params.row.machineModel || "";
        return manufacturer && modelName ? `${manufacturer} - ${modelName}` : manufacturer || modelName;
      },
    },
    {
      field: "machineMaintenanceCode",
      headerName: "Maint Code",
      minWidth: 120,
      editType: "dropdownedit",
      textAlign: "left",
      flex: 0.4,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Maint Code"
          value={filters.maintenanceCode}
          options={maintenanceCodes}
          onChange={(value) => setFilters({ ...filters, maintenanceCode: value })}
        />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 90,
      editType: "dropdownedit",
      textAlign: "left",
      renderHeader: () => <ColumnHeaderLabel label="Description" />,
      flex: 0.35,
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 125,
      flex: 0.4,
      renderCell: (params) => appConstants?.allTypes?.ABBREVIATIONS?.[params.value] || params.value,
      renderHeader: () => {
        const abbreviatedOptions = categories.map((category) => appConstants?.allTypes?.ABBREVIATIONS?.[category] || category);
        const displayValue = filters.category ? appConstants?.allTypes?.ABBREVIATIONS?.[filters.category] || filters.category : "";
        return (
          <FilterHeaderDropdown
            label="Category"
            value={displayValue}
            options={abbreviatedOptions}
            onChange={(abbreviatedValue) => {
              if (!abbreviatedValue) {
                setFilters({ ...filters, category: "" });
                return;
              }
              const originalCategory =
                categories.find((category) => appConstants?.allTypes?.ABBREVIATIONS?.[category] === abbreviatedValue) || abbreviatedValue;
              setFilters({ ...filters, category: originalCategory });
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 0.4,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Status"
          value={filters.status}
          options={statuses}
          onChange={(value) => setFilters({ ...filters, status: value })}
        />
      ),
    },
    {
      field: "vendor",
      headerName: "Vendor",
      minWidth: 130,
      flex: 0.4,
      renderHeader: () => (
        <FilterHeaderDropdown
          label="Vendor"
          value={filters.vendor}
          options={vendors}
          onChange={(value) => setFilters({ ...filters, vendor: value })}
        />
      ),
      valueGetter: (params) => getVendorDisplayName(params.row),
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 160,
      minWidth: 100,
      maxWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: renderGridActions,
      disableClickEventBubbling: true,
    },
  ];
  const downloadCsv = (e, exportAll = false) => {
    e.preventDefault();
    const serviceTicketsList = [];
    if (exportAll) {
      allServiceTickets.data.forEach((ticket) => {
        const machine = machinesData.find((machine) => machine.id === ticket?.machine?.id);
        const assigneUser = appConstants?.appUsers?.find((user) => ticket?.assignees?.[0] === user.userId);
        const assignerUser = appConstants?.appUsers?.find(
          (user) => ticket?.assignerId === user.userId && user.userRole === appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR,
        );
        const creatorUser = appConstants?.appUsers?.find((user) => ticket?.creatorId === user.userId);
        return serviceTicketsList.push({
          ticketId: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
          ticketCategory: ticket?.ticketCategory,
          status: ticket?.status,
          machineManufacturer: machine?.manufacturer || "",
          machineModel: machine?.model || "",
          machineSerialNumber: machine?.serialNumber || "",
          machineMaintenanceCode: machine?.maintenanceCode || "",
          description: ticket?.description || "",
          dueDate: moment(ticket.dueDate).format("DD MMM YYYY"),
          dateCreated: moment(ticket.openDate).format("DD MMM YYYY"),
          scheduledDate: moment(ticket.scheduledDate).format("DD MMM YYYY"),
          creator: creatorUser?.name || "",
          assigner: assignerUser?.name || "",
          technician: assigneUser?.name,
        });
      });
    }
    const headers = [
      "Ticket Id,Open Date,Scheduled Date,Creator,Supervisor,Technician,Maintenance Code,Description,Manufacturer,Model,Serial Number,Category,Status",
    ];
    const keys = [
      "ticketId",
      "dateCreated",
      "scheduledDate",
      "creator",
      "assigner",
      "technician",
      "machineMaintenanceCode",
      "description",
      "machineManufacturer",
      "machineModel",
      "machineSerialNumber",
      "ticketCategory",
      "status",
    ];
    const dataToExport = exportAll
      ? serviceTicketsList
      : serviceTickets.map((ticket) => ({
          ...ticket,
          ticketId: appConstants?.showServiceTicketId ? ticket.ticketId : ticket.ticketNumber || ticket.ticketId,
        }));
    exportToCsv(headers, keys, dataToExport, "ServiceTickets");
    setShowExportAlertDialog(false);
  };

  if (isLoading)
    return (
      <div
        style={{
          textAlign: "center",
          paddingTop: 200,
        }}
      >
        <CircularProgress color="secondary" />
      </div>
    );
  return (
    <>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"} my={2}>
          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2}>
            <Box>
              <Button
                startIcon={<AddIcon />}
                sx={{ height: "self" }}
                autoCapitalize="none"
                variant="contained"
                onClick={() =>
                  navigate(`/${Links.CREATE_SERVICE_TICKET}/`, {
                    state: { isCreatingTicket: true },
                  })
                }
              >
                Create
              </Button>
            </Box>

            <OutlinedInput
              id="outlined-adornment-password"
              value={searchValue}
              onChange={(event) => handleSearchChange(event)}
              placeholder="Search..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchValue && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} edge="end" aria-label="Clear search">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
            />

            <Paper>
              <Stack
                direction={"row"}
                backgroundColor={
                  filterOptions && filterOptions.overDue
                    ? mode === "dark"
                      ? theme.palette.grey[700]
                      : theme.palette.primary[700]
                    : mode === "dark"
                      ? theme.palette.grey[10]
                      : theme.palette.primary[200]
                }
                alignItems={"center"}
                px={0.5}
                py={filterOptions && filterOptions.overDue ? 0 : 0.3}
              >
                <Tooltip title="Filter over due tickets">
                  <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: true })} size="small">
                    <Typography sx={{ color: "#fff" }}>Over due</Typography>
                  </IconButton>
                </Tooltip>
                {filterOptions && filterOptions.overDue ? (
                  <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: false })}>
                    <Tooltip title="Clear filter">
                      <CloseIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Tooltip>
                  </IconButton>
                ) : null}
              </Stack>
            </Paper>

            <Button startIcon={<CloseIcon />} sx={{ height: "self" }} autoCapitalize="none" variant="contained" onClick={clearAllFilters}>
              Clear Filters
            </Button>
          </Stack>
          <Stack>
            <Button
              startIcon={<DownloadIcon />}
              sx={{ minWidth: 170 }}
              autoCapitalize="none"
              variant="contained"
              disabled={
                !serviceTickets.filter((item) => Object.values(item).join(" ").toLowerCase().includes(searchValue.toLowerCase())).length
              }
              onClick={(e) => {
                const ifFilterExists = Object.keys(filterOptions).filter((item) =>
                  filterOptions[item]
                    ? filterOptions[item] === true || filterOptions[item] > 0
                      ? filterOptions[item]
                      : filterOptions[item].length
                    : filterOptions[item],
                );
                ifFilterExists.length ? setShowExportAlertDialog(true) : downloadCsv(e);
              }}
            >
              Export to csv
            </Button>
          </Stack>
        </Stack>
        <Box
          sx={{
            "& .dataGridTable": {
              height: "750px !important",
            },
          }}
        >
          <DataGridTable
            loading={loading}
            rowId={"ticketId"}
            data={serviceTickets.filter((item) => Object.values(item).join(" ").toLowerCase().includes(searchValue.toLowerCase()))}
            columnAttributes={columnAttributes}
          />
        </Box>

        <Dialog open={showExportAlertDialog} onClose={() => setShowExportAlertDialog(false)}>
          <DialogContent>
            <Typography mt={1}>Click "Export filtered" to export only the filtered tickets.</Typography>
            <Typography mt={1}>Click "Export all" to export all tickets.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExportAlertDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e)}>
              Export filtered
            </Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e, true)}>
              Export all
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default AllServiceTickets;
